import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { AuthRequired } from "@/components/auth/AuthRequired";
import { SubPage } from "@/components/profile/SettingsKit";
import { deletionEligibility, deleteAccount } from "@/lib/auth";
import { keys } from "@/lib/queries";
import { humanizeError } from "@/lib/errorMessages";
import { Card, CardHeader, CardTitle, CardBody, Button, Input, Skeleton } from "@/components/ui";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/_app/settings_/delete")({
  head: () =>
    buildHead({ path: "/settings/delete", title: "Delete account", noindex: true }),
  component: DeleteAccountPage,
});

function DeleteAccountPage() {
  const { t } = useTranslation("account");
  return (
    <AuthRequired>
      <SubPage title={t("delete.title")} subtitle={t("delete.subtitle")}>
        <DeleteAccountPanel />
      </SubPage>
    </AuthRequired>
  );
}

function DeleteAccountPanel() {
  const { t } = useTranslation("account");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const eligQ = useQuery({ queryKey: ["me", "deletion-eligibility"], queryFn: deletionEligibility });
  const [confirmText, setConfirmText] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => deleteAccount(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.me() });
      navigate({ to: "/" });
    },
    onError: (e) => {
      // A 409 means new blockers appeared since the page loaded — surface
      // the message and refresh the eligibility list.
      setErr(humanizeError(e));
      void eligQ.refetch();
    },
  });

  const elig = eligQ.data;
  const blockers = elig?.blockers ?? [];
  const eligible = !!elig?.eligible;
  const confirmed = confirmText.trim().toUpperCase() === "DELETE";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("delete.beforeYouGo")}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-fg-muted">{t("delete.explainer")}</p>
          {eligQ.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : blockers.length === 0 ? (
            <p className="text-sm text-success">{t("delete.allClear")}</p>
          ) : (
            <ul className="space-y-2">
              {blockers.map((b, idx) => (
                <li key={idx} className="rounded-lg border border-border bg-surface-2 p-3 text-sm">
                  <div className="text-fg">{b.message}</div>
                  {/* Self-service blockers deep-link to where the user fixes them.
                      A wallet balance can only be removed by an admin/backoffice
                      withdrawal, so it gets a contact hint, not a dead "go resolve"
                      link. Pending transfers resolve themselves once they settle. */}
                  {b.type === "WALLET_BALANCE" ? (
                    <p className="mt-1 text-xs font-medium text-sky-500">{t("delete.walletHint")}</p>
                  ) : (b.type === "OPEN_POSITIONS" || b.type === "OPEN_ORDERS") && b.deep_link ? (
                    <button
                      type="button"
                      className="mt-1 text-xs font-medium text-primary hover:underline"
                      onClick={() => navigate({ to: b.deep_link as never })}
                    >
                      {t("delete.goResolve")}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-danger">{t("delete.title")}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-fg-muted">
            <Trans t={t} i18nKey="delete.typeToConfirm" components={{ keyword: <span className="font-mono font-medium text-fg" /> }} />
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            disabled={!eligible}
            autoComplete="off"
          />
          {err && <p className="text-sm text-danger">{err}</p>}
          <Button
            variant="destructive"
            block
            disabled={!eligible || !confirmed}
            loading={del.isPending}
            onClick={() => {
              setErr(null);
              del.mutate();
            }}
          >
            {t("delete.confirmButton")}
          </Button>
          {!eligible && !eligQ.isLoading && (
            <p className="text-xs text-fg-muted">{t("delete.resolveFirst")}</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
